import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import styles from './comma-separated-list.module.scss';

export interface CommaSeparatedListProps {
  /** The class name to apply */
  className?: string;
  /** The content to show */
  list: string[];
}

const MIN_CONTAINER_WIDTH = 250;

const getSeparator = (visibleCount: number) => {
  if (visibleCount === 0) return '';

  if (visibleCount === 1) return ' ';

  return ', ';
};

const formatVisibleItems = (items: string[]) => {
  if (items.length <= 1) return items.join('');

  return items.reduce((result, item, index) => {
    if (index === 0) {
      return item;
    }

    return `${result}${getSeparator(index)}${item}`;
  }, '');
};

const CommaSeparatedList = ({
  className,
  list = [],
  ...rest
}: CommaSeparatedListProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleItems, setVisibleItems] = useState<string[]>([]);
  const [remainingCount, setRemainingCount] = useState(0);

  useEffect(() => {
    const calculateVisibleItems = () => {
      if (!containerRef.current) {
        return;
      }

      const container = containerRef.current;
      const containerWidth =
        container.offsetWidth === 0
          ? MIN_CONTAINER_WIDTH
          : container.offsetWidth;
      let currentWidth = 0;
      const fitItems: string[] = [];

      const temporarySpan = document.createElement('span');

      temporarySpan.style.visibility = 'hidden';
      temporarySpan.style.position = 'absolute';
      temporarySpan.style.whiteSpace = 'nowrap';
      container.appendChild(temporarySpan);

      for (let index = 0; index < list.length; index++) {
        const item = list[index];

        temporarySpan.innerText = `${getSeparator(fitItems.length)}${item}`;
        const itemWidth = temporarySpan.offsetWidth;

        const remainingAfterCurrentItem = list.length - (index + 1);
        let suffixWidth = 0;

        if (remainingAfterCurrentItem > 0) {
          temporarySpan.innerText = `, +${remainingAfterCurrentItem}`;
          suffixWidth = temporarySpan.offsetWidth;
        }

        if (currentWidth + itemWidth + suffixWidth > containerWidth) {
          break;
        } else {
          fitItems.push(item);
          currentWidth += itemWidth;
        }
      }

      container.removeChild(temporarySpan);
      const overflowCount = list.length - fitItems.length;

      setVisibleItems(fitItems);
      setRemainingCount(overflowCount);
    };

    calculateVisibleItems();

    const resizeObserver = new ResizeObserver(calculateVisibleItems);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', calculateVisibleItems);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateVisibleItems);
    };
  }, [list]);

  return (
    <div
      ref={containerRef}
      {...rest}
      className={classNames(styles.component, className)}
    >
      <Typography className={styles.itemsContainer} tagStyle="bodyMedium">
        {formatVisibleItems(visibleItems)}

        {remainingCount > 0 && `, +${remainingCount}`}
      </Typography>
    </div>
  );
};

CommaSeparatedList.displayName = 'CommaSeparatedList';

export default CommaSeparatedList;
